import type { SaccadeAssets } from "./assets";
import { CENTER, countTargets, fitRidge, lambdaFor, meanEmbedding } from "./grids";
import { Landmarker } from "./landmarker";
import { OrtEmbeddingModel } from "./model";
import type { FrameTime, TrackerFrame } from "./pipeline";
import { Pipeline } from "./pipeline";
import type { SaccadeProgress, SaccadeProgressCallback } from "./progress";
import { reportProgress } from "./progress";
import type { CalHead, CalPoint, CalWeighting, EmbeddingModel, Gaze, ModelIdentity } from "./types";

export interface SaccadeTrackerOptions {
  assets?: SaccadeAssets;
  /** getUserMedia ideals. Default 640x480, user-facing. */
  video?: { width?: number; height?: number };
  /**
   * How many consecutive camera frames are averaged into one gaze estimate. Default 1, which
   * predicts from the newest frame alone: the lowest latency, and the only sane setting for a
   * gaze-contingent design. Raising it trades latency for a steadier estimate — the reported
   * gaze then refers to `time.meanCapture`, roughly `(n − 1) / 2` frames in the past.
   */
  smoothingFrames?: number;
  /** ONNX Runtime execution providers, tried in order. Default ["webgpu", "wasm"]. */
  executionProviders?: ("webgpu" | "wasm")[];
  onFrame?: (f: TrackerFrame) => void;
  /**
   * Called as `init()` walks its load stages, so a page can show a setup screen instead of a
   * blank wait. The `model` stage carries byte counts for the ~20 MB `.onnx`. Optional: with
   * no callback nothing about init changes. See `SaccadeProgress`, and `onProgress()` for
   * subscribing after construction.
   */
  onProgress?: SaccadeProgressCallback;
  /**
   * Extension to the published contract: use this camera stream instead of calling
   * getUserMedia. Useful when the page already owns the camera.
   */
  stream?: MediaStream;
  /** Extension: substitute the embedding model (tests, or a pre-warmed session). */
  model?: EmbeddingModel;
  /**
   * Weight calibration rows with this logistic head over the embedding.
   *
   * Off by default: a head is trained against one model's embedding space and produces
   * meaningless scores on another model's embeddings. Pass `CAL_HEAD` to reproduce how
   * eye-embedding 1.0.0 weighted its rows. A model that emits its own weights needs nothing
   * here -- that path is automatic, and this option overrides it when both are present.
   */
  calHead?: CalHead | null;
}

export interface InitResult {
  ep: "webgpu" | "wasm";
  videoWidth: number;
  videoHeight: number;
}

const LUM_W = 64;
const LUM_H = 48;

/** How often the tracker re-checks that its video is still in the document, in ms. */
const ATTACH_CHECK_MS = 1000;

/**
 * Camera + face landmarks + eye embedding + calibration, as one object.
 *
 * Nothing happens until `init()` (which asks for the camera); after that `start()` runs the
 * frame loop and every subscriber gets a `TrackerFrame` per camera frame. `gaze` stays null
 * until calibration points have been added and `fitCalibration()` has run.
 */
export class SaccadeTracker {
  /**
   * The camera element. The tracker keeps this element in the document while running, because
   * Chrome only delivers camera frames (`requestVideoFrameCallback`) for a video that is
   * actually rendered. Move it wherever you like — into a preview panel, a corner of the page —
   * but never `display: none` it and never detach it from the document while the tracker is
   * running; to hide it, use `opacity: 0` (and/or a 2x2 px size) instead. If it is not in the
   * document, the tracker puts it back into a tiny invisible holder of its own on `document.body`.
   */
  readonly video: HTMLVideoElement;
  private opts: SaccadeTrackerOptions;
  private assets: SaccadeAssets;
  private landmarker: Landmarker | null = null;
  private model: EmbeddingModel | null = null;
  private pipeline: Pipeline | null = null;
  private stream: MediaStream | null = null;
  private ownsStream = false;
  private initPromise: Promise<InitResult> | null = null;
  private initResult: InitResult | null = null;
  private cal: CalPoint[] = [];
  private kernel: Float32Array | null = null;
  private weighting: CalWeighting | null = null;
  private lastGaze: { gaze: Gaze; time: FrameTime } | null = null;
  private subscribers = new Set<(f: TrackerFrame) => void>();
  private progressListeners = new Set<SaccadeProgressCallback>();
  private lastProgress: SaccadeProgress | null = null;
  private smoothingFrames: number;
  private wantRunning = false;
  private disposed = false;
  private lumCanvas: HTMLCanvasElement | null = null;
  private lumCtx: CanvasRenderingContext2D | null = null;
  private holder: HTMLDivElement | null = null;
  private attachTimer: ReturnType<typeof setInterval> | null = null;

  constructor(opts: SaccadeTrackerOptions = {}) {
    this.opts = opts;
    this.assets = opts.assets ?? {};
    this.smoothingFrames = opts.smoothingFrames ?? 1;
    if (opts.onFrame) this.subscribers.add(opts.onFrame);
    if (opts.onProgress) this.progressListeners.add(opts.onProgress);
    this.video = document.createElement("video");
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute("playsinline", "");
  }

  /** Requests the camera, loads MediaPipe + ONNX and warms them up. Idempotent. */
  init(): Promise<InitResult> {
    if (!this.initPromise) {
      this.initPromise = this.doInit();
      this.initPromise.catch(() => {
        this.initPromise = null;
      });
    }
    return this.initPromise;
  }

  get initialized(): boolean {
    return this.initResult !== null;
  }

  private async doInit(): Promise<InitResult> {
    if (this.disposed) throw new Error("tracker disposed");
    const onProgress = this.emitProgress;
    const width = this.opts.video?.width ?? 640;
    const height = this.opts.video?.height ?? 480;
    if (this.opts.stream) {
      this.stream = this.opts.stream;
      this.ownsStream = false;
    } else {
      reportProgress(onProgress, { stage: "camera" });
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: width }, height: { ideal: height }, facingMode: "user" },
        audio: false,
      });
      this.ownsStream = true;
    }
    // Before play(): Chrome delivers frames only to a rendered video element.
    this.watchAttachment();
    this.video.srcObject = this.stream;
    try {
      await this.video.play();
    } catch {
      // Autoplay policy, or a runtime without a real media element: the frame loop only needs
      // frames to arrive, and they do as soon as the stream is attached.
    }
    if (!this.video.videoWidth) {
      await new Promise<void>((resolve) => {
        const done = (): void => resolve();
        this.video.addEventListener("loadedmetadata", done, { once: true });
        // jsdom and some headless setups never fire it; do not hang the caller.
        setTimeout(done, 3000);
      });
    }

    this.landmarker = await Landmarker.create({ assets: this.assets, onProgress });
    let ep: "webgpu" | "wasm" = "wasm";
    if (this.opts.model) {
      this.model = this.opts.model;
      ep = (await this.model.init()).ep;
    } else {
      const model = new OrtEmbeddingModel({
        assets: this.assets,
        executionProviders: this.opts.executionProviders,
        onProgress,
      });
      ep = (await model.init()).ep;
      this.model = model;
    }

    this.pipeline = new Pipeline(this.video, this.landmarker, this.model, {
      smoothingFrames: this.smoothingFrames,
      center: CENTER,
      onFrame: (f) => this.handleFrame(f),
    });
    if (this.kernel) this.pipeline.setKernel(this.kernel);

    this.initResult = {
      ep,
      videoWidth: this.video.videoWidth,
      videoHeight: this.video.videoHeight,
    };
    if (this.wantRunning) this.pipeline.start();
    reportProgress(onProgress, { stage: "ready" });
    return this.initResult;
  }

  private handleFrame(f: TrackerFrame): void {
    if (f.gaze) this.lastGaze = { gaze: f.gaze, time: f.time };
    for (const cb of Array.from(this.subscribers)) cb(f);
  }

  /**
   * Put `video` back in the document if it is not there.
   *
   * Chrome only fires `requestVideoFrameCallback` for a video element that is rendered — in the
   * document and not `display: none` — so a host that unmounts the element (a step-based UI
   * swapping screens, a jsPsych plugin clearing its display) silently stops every camera frame.
   * The holder is rendered but invisible and inert: no `display: none`, no `visibility: hidden`,
   * either of which would stop the frames just as effectively.
   */
  private ensureAttached(): void {
    if (this.disposed) return;
    if (typeof document === "undefined" || !document.body) return;
    if (this.video.isConnected) return;
    if (!this.holder) {
      const holder = document.createElement("div");
      holder.setAttribute("data-saccade-video-holder", "");
      const s = holder.style;
      s.position = "fixed";
      s.left = "0";
      s.top = "0";
      s.width = "2px";
      s.height = "2px";
      s.opacity = "0";
      s.pointerEvents = "none";
      s.overflow = "hidden";
      s.zIndex = "-1";
      this.holder = holder;
    }
    if (!this.holder.isConnected) document.body.appendChild(this.holder);
    this.holder.appendChild(this.video);
  }

  /**
   * Keep `video` attached from here on. A host can detach it at any time (a screen change, a
   * cleared jsPsych display), and `runLoopback` pumps frames while the tracker is *stopped*, so
   * the watch is tied to the camera's lifetime rather than to the frame loop.
   */
  private watchAttachment(): void {
    this.ensureAttached();
    if (this.attachTimer == null && typeof setInterval === "function") {
      this.attachTimer = setInterval(() => this.ensureAttached(), ATTACH_CHECK_MS);
    }
  }

  /** Start the frame loop. Safe before `init()`: the loop starts as soon as init finishes. */
  start(): void {
    this.wantRunning = true;
    this.watchAttachment();
    this.pipeline?.start();
  }

  stop(): void {
    this.wantRunning = false;
    this.pipeline?.stop();
  }

  get running(): boolean {
    return this.pipeline ? this.pipeline.running : false;
  }

  /** Stop everything and release the camera. The tracker cannot be re-initialised after this. */
  /**
   * What model actually ran, derived from the loaded bytes -- see {@link ModelIdentity}.
   * Null before `init()`, and null for a substituted model that does not report one.
   */
  getModelIdentity(): ModelIdentity | null {
    return this.model?.identity?.() ?? null;
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    if (this.attachTimer != null) {
      clearInterval(this.attachTimer);
      this.attachTimer = null;
    }
    this.pipeline = null;
    this.landmarker?.close();
    this.landmarker = null;
    const model = this.model as { dispose?: () => void } | null;
    model?.dispose?.();
    this.model = null;
    if (this.stream && this.ownsStream) for (const t of this.stream.getTracks()) t.stop();
    this.stream = null;
    this.video.srcObject = null;
    if (this.holder) {
      this.holder.remove();
      this.holder = null;
    }
    this.subscribers.clear();
    this.progressListeners.clear();
    this.lastProgress = null;
    this.initResult = null;
    this.initPromise = null;
  }

  /** Subscribe to every frame. Returns the unsubscribe function. */
  onFrame(cb: (f: TrackerFrame) => void): () => void {
    this.subscribers.add(cb);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  /**
   * Subscribe to `init()`'s load progress. The most recent report, if there is one, is delivered
   * straight away, so a subscriber that arrives while init is under way is not left waiting for
   * the next stage. Returns the unsubscribe function.
   */
  onProgress(cb: SaccadeProgressCallback): () => void {
    this.progressListeners.add(cb);
    if (this.lastProgress) reportProgress(cb, this.lastProgress);
    return () => {
      this.progressListeners.delete(cb);
    };
  }

  private emitProgress = (p: SaccadeProgress): void => {
    this.lastProgress = p;
    for (const cb of Array.from(this.progressListeners)) reportProgress(cb, p);
  };

  /** Resolves with the next frame the loop emits. */
  nextFrame(): Promise<TrackerFrame> {
    if (!this.pipeline) return this.afterInit((p) => p.nextFrame());
    return this.pipeline.nextFrame();
  }

  async nextEmbedding(): Promise<Float32Array | null> {
    return (await this.nextFrame()).embedding;
  }

  /**
   * The next frame's embedding together with the model's weight for it. Calibration collects
   * through this rather than `nextEmbedding()`, keeping the weight paired with the embedding
   * it describes.
   */
  async nextSample(): Promise<{ embedding: Float32Array; weight: number | null } | null> {
    const f = await this.nextFrame();
    return f.embedding ? { embedding: f.embedding, weight: f.weight } : null;
  }

  async nextGaze(): Promise<Gaze | null> {
    return (await this.nextFrame()).gaze;
  }

  private async afterInit<T>(fn: (p: Pipeline) => Promise<T>): Promise<T> {
    await this.init();
    if (!this.pipeline) throw new Error("tracker not initialised");
    return fn(this.pipeline);
  }

  // ---------------------------------------------------------------- calibration

  /**
   * Add one calibration target and the embeddings collected while the subject looked at it.
   *
   * `weights` are the model's per-frame scores for those embeddings, when it emits any. They
   * weight the point's mean embedding and, averaged, become its row weight in the fit. Pass
   * nothing and the point goes in unweighted.
   */
  addCalibrationPoint(target: Gaze, embeddings: Float32Array[], weights?: number[] | null): void {
    if (embeddings.length === 0) return;
    const w = weights ?? null;
    this.cal.push({ target, embeddings, weights: w, meanEmbedding: meanEmbedding(embeddings, w) });
  }

  clearCalibration(): void {
    this.cal = [];
    this.kernel = null;
    this.weighting = null;
    this.lastGaze = null;
    this.pipeline?.setKernel(null);
  }

  getCalibrationPoints(): CalPoint[] {
    return this.cal.slice();
  }

  /** Solve the ridge map from the points added so far. Null when there is nothing to fit. */
  fitCalibration(opts: { lambda?: number; center?: number; calHead?: CalHead | null } = {}): {
    lambda: number;
    nPoints: number;
    weighting: CalWeighting;
  } | null {
    if (this.cal.length === 0) return null;
    const nPoints = countTargets(this.cal);
    const lambda = opts.lambda ?? lambdaFor(nPoints);
    const center = opts.center ?? CENTER;
    const head = opts.calHead !== undefined ? opts.calHead : (this.opts.calHead ?? null);
    const { kernel, weighting } = fitRidge(this.cal, head, lambda, center);
    this.kernel = kernel;
    this.weighting = weighting;
    this.pipeline?.setCenter(center);
    this.pipeline?.setKernel(this.kernel);
    return { lambda, nPoints, weighting };
  }

  /** How the last fit weighted its rows, or null before one has run. */
  getCalWeighting(): CalWeighting | null {
    return this.weighting;
  }

  get calibrated(): boolean {
    return this.kernel != null;
  }

  /** The fitted kernel, `2 * d` long (x and y interleaved), or null. */
  getKernel(): Float32Array | null {
    return this.kernel;
  }

  setSmoothingFrames(n: number): void {
    this.smoothingFrames = Math.max(1, Math.round(n));
    this.pipeline?.setSmoothingFrames(this.smoothingFrames);
  }

  getSmoothingFrames(): number {
    return this.smoothingFrames;
  }

  /** Latest gaze (viewport fractions) and the times of the frame it came from, or null. */
  getCurrentGaze(): { gaze: Gaze; time: FrameTime } | null {
    return this.lastGaze;
  }

  /**
   * Mean luminance of the whole current camera frame (BT.601 on a 64x48 downsample). This is
   * what the timing loopback correlates against the screen; it is public so plugins can reuse
   * the tracker's video instead of opening a second stream.
   */
  sampleLuminance(): number {
    if (!this.lumCanvas) {
      this.lumCanvas = document.createElement("canvas");
      this.lumCanvas.width = LUM_W;
      this.lumCanvas.height = LUM_H;
      this.lumCtx = this.lumCanvas.getContext("2d", { willReadFrequently: true });
    }
    const ctx = this.lumCtx;
    if (!ctx) return NaN;
    ctx.drawImage(this.video, 0, 0, LUM_W, LUM_H);
    const d = ctx.getImageData(0, 0, LUM_W, LUM_H).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) {
      sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    }
    return sum / (LUM_W * LUM_H);
  }
}
